import buildQuery, { Expand, Filter, QueryOptions, Select } from 'odata-query';
// `config` se importa DIRECTO y no por el contenedor a proposito, para romper un ciclo de imports:
// este fichero lo importan los 32 servicios de `src/api/**`, y el contenedor los importa a todos.
// Con `import container from 'src/config/inversify-config'` el ciclo era
//   countries-api -> api-service -> inversify-config -> countries-api
// y bastaba con que un modulo se evaluara a medias para que el contenedor se quedara SIN registrar
// bindings. El sintoma no se parecia en nada a la causa: «Cannot access 'QueryApiService' before
// initialization», o «No matching bindings found for serviceIdentifier: PersonProfileApiService» en
// `onboarding/layout/layout.tsx`, y detras un «Maximum update depth exceeded» dentro de un
// componente de antd, que era solo el limite de error recreando el arbol una y otra vez.
//
// No cambia el comportamiento: el contenedor registra AppConfig con
// `toConstantValue(config)` (`inversify-config.ts:41`), ese mismo `config`, y nadie lo reenlaza.
import config from 'src/config/app-config';
import axios from 'axios';
import querystring from 'query-string';
import { isNullOrWhiteSpaces } from 'src/shared/utils/strings';
import { getKeyValuePairs } from 'src/shared/utils/objects';
import { AxiosResponse } from 'axios';

export interface QueryResult<T> {
  count: number;
  items: T[];
}

export interface ODataResult<T> {
  '@odata.count': number;
  value: T[];
}

export interface CommandResult {
  isSuccess: boolean;
  error: string;
  identity: string;
}

export interface ItemCommandResult<T> {
  item: T;
  isSuccess: boolean;
  error: string;
  identity: string;
}

export abstract class QueryApiService<TEntity> {
  protected _odataBaseUrl: string;
  protected _apiBaseUrl: string;

  constructor(controllerName: string, version: string = 'v1') {
    const baseUrl = config.baseUrl;
    this._odataBaseUrl = `${baseUrl}/odata/${version}/${controllerName}`;
    this._apiBaseUrl = `${baseUrl}/api/${version}/${controllerName}`;
  }

  public async getWithOData<T = TEntity>(
    options?: Partial<QueryOptions<T>>,
    params?: { [key: string]: any },
    action = '',
    useWebApi: boolean = false
  ): Promise<ODataResult<T>> {
    const parts: string[] = [];
    if (options) {
      const oQs = buildQuery(options);
      if (!isNullOrWhiteSpaces(oQs)) parts.push(oQs);
    }
    if (params) {
      const pQs = querystring.stringify(params);
      if (!isNullOrWhiteSpaces(pQs)) parts.push(pQs);
    }

    parts.push('$format=application/json;odata.metadata=none');

    let qs = '';

    if (parts.length > 0) {
      qs = parts.join('&');
      if (!qs.startsWith('?')) qs = '?' + qs;
    }

    const result = await axios.get<ODataResult<T>>(
      `${useWebApi ? this._apiBaseUrl : this._odataBaseUrl}${isNullOrWhiteSpaces(action) ? '' : `/${action}`}${qs}`
    );

    return result?.data;
  }

  public async getOneWithOData<T = TEntity>(
    options?: { select?: Select<T>; expand?: Expand<T>; filter?: Filter<T> },
    params?: { [key: string]: any },
    action = '',
    useWebApi: boolean = false
  ): Promise<T> {
    const parts: string[] = [];
    if (options) {
      const oQs = buildQuery(options);
      if (!isNullOrWhiteSpaces(oQs)) parts.push(oQs);
    }
    if (params) {
      const pQs = querystring.stringify(params);
      if (!isNullOrWhiteSpaces(pQs)) parts.push(pQs);
    }

    parts.push('$format=application/json;odata.metadata=none');

    let qs = '';

    if (parts.length > 0) {
      qs = parts.join('&');
      if (!qs.startsWith('?')) qs = '?' + qs;
    }

    const result = await axios.get<T>(`${useWebApi ? this._apiBaseUrl : this._odataBaseUrl}${isNullOrWhiteSpaces(action) ? '' : `/${action}`}${qs}`);

    return result?.data;
  }

  public async getById<TId, T = TEntity>(id: TId, options?: { select?: Select<T>; expand?: Expand<T> }, params?: { [key: string]: any }): Promise<T> {
    const parts: string[] = [];
    if (options) {
      const oQs = buildQuery(options);
      if (!isNullOrWhiteSpaces(oQs)) parts.push(oQs);
    }
    if (params) {
      const pQs = querystring.stringify(params);
      if (!isNullOrWhiteSpaces(pQs)) parts.push(pQs);
    }

    parts.push('$format=application/json;odata.metadata=none');

    let qs = '';

    if (parts.length > 0) {
      qs = parts.join('&');
      if (!qs.startsWith('?')) qs = '?' + qs;
    }

    const result = await axios.get<T>(`${this._odataBaseUrl}/${encodeURIComponent(`${id}`)}${qs}`);

    return result?.data;
  }

  public async get<T>(action: string = '', params?: { [key: string]: any }): Promise<T> {
    const parts: string[] = [];

    if (params) {
      const pQs = querystring.stringify(params);
      if (!isNullOrWhiteSpaces(pQs)) parts.push(pQs);
    }

    parts.push('$format=application/json;odata.metadata=none');

    let qs = '';

    if (parts.length > 0) {
      qs = parts.join('&');
      if (!qs.startsWith('?')) qs = '?' + qs;
    }

    const result = await axios.get<T>(`${this._apiBaseUrl}/${action}${qs}`);
    return result?.data;
  }
}
export abstract class FormApiService {
  protected _odataBaseUrl: string;
  protected _apiBaseUrl: string;

  constructor(controllerName: string, version: string = 'v1') {
    const baseUrl = config.baseUrl;
    this._odataBaseUrl = `${baseUrl}/odata/${version}/${controllerName}`;
    this._apiBaseUrl = `${baseUrl}/api/${version}/${controllerName}`;
  }

  public async post<TBody, TResult>(body: TBody, action: string = '', useForm: boolean = false): Promise<TResult> {
    if (useForm) {
      const formData = new FormData();
      getKeyValuePairs(body).forEach(({ key, value }) => formData.append(key, value as any));
      /*
            const result = await axios({
            method: 'post',
            url: `${baseUrl}odata/CulturalEvents`,
            data: formData,
        });
        */
      const result = await axios.post<TResult, AxiosResponse<TResult>, FormData>(`${this._apiBaseUrl}/${action}`, formData);
      return result?.data;
    } else {
      const result = await axios.post<TResult>(`${this._apiBaseUrl}/${action}`, body);
      return result?.data;
    }
  }

  public async put<TBody, TResult>(body: TBody, action: string = '', useForm: boolean = false): Promise<TResult> {
    if (useForm) {
      const formData = new FormData();
      getKeyValuePairs(body).forEach(({ key, value }) => formData.append(key, value as any));
      /*
            const result = await axios({
            method: 'post',
            url: `${baseUrl}odata/CulturalEvents`,
            data: formData,
        });
        */
      const result = await axios.put<TResult, AxiosResponse<TResult>, FormData>(
        `${this._apiBaseUrl}/${!isNullOrWhiteSpaces(action) ? `${action}/` : ''}`,
        formData
      );
      return result?.data;
    } else {
      const result = await axios.put<TResult>(`${this._apiBaseUrl}/${!isNullOrWhiteSpaces(action) ? `${action}/` : ''}`, body);
      return result?.data;
    }
  }

  public async putById<TId = string | number, TBody = any, TResult = any>(
    id: TId,
    body: TBody,
    action: string = '',
    useForm: boolean = false
  ): Promise<TResult> {
    if (useForm) {
      const formData = new FormData();
      getKeyValuePairs(body).forEach(({ key, value }) => formData.append(key, value as any));
      /*
            const result = await axios({
            method: 'post',
            url: `${baseUrl}odata/CulturalEvents`,
            data: formData,
        });
        */
      const result = await axios.put<TResult, AxiosResponse<TResult>, FormData>(
        `${this._apiBaseUrl}/${!isNullOrWhiteSpaces(action) ? `${action}/` : ''}${encodeURIComponent(`${id}`)}`,
        formData
      );
      return result?.data;
    } else {
      const result = await axios.put<TResult>(
        `${this._apiBaseUrl}/${!isNullOrWhiteSpaces(action) ? `${action}/` : ''}${encodeURIComponent(`${id}`)}`,
        body
      );
      return result?.data;
    }
  }

  public async delete<TId = string | number, TResult = any>(id: TId, action: string = '', body?: any): Promise<TResult> {
    if (body) {
      const result = await axios.request<TResult>({
        url: `${this._apiBaseUrl}/${!isNullOrWhiteSpaces(action) ? `${action}/` : ''}${encodeURIComponent(`${id}`)}`,
        data: body,
        method: 'DELETE'
      });
      return result?.data;
    } else {
      const result = await axios.delete<TResult>(
        `${this._apiBaseUrl}/${!isNullOrWhiteSpaces(action) ? `${action}/` : ''}${encodeURIComponent(`${id}`)}`
      );
      return result?.data;
    }
  }
}
